import { Link } from 'react-router'
import { useCopy, useLocale } from '../../content'
import { COMUNI } from '../../data/comuni'
import { grouped } from '../../data/format'
import { pathFor } from '../../routes'
import { Eyebrow } from '../layout/Eyebrow'
import { Section } from '../layout/Section'
import { ZoneMap } from './ZoneMap'

/** The figures beside the map, computed from the service area so they cannot drift from it. */
const VALLEY_COUNT = COMUNI.filter((c) => c.focus !== 'lake').length
const ALTITUDES = COMUNI.flatMap((c) => (c.altitude === null ? [] : [c.altitude]))
const DISTANCES = COMUNI.flatMap((c) => (c.distanceKm === null ? [] : [c.distanceKm]))

/**
 * The home's zone band, framed by the cinema bars (`<Section letterbox>`).
 *
 * It follows the (paper) second-homes band on paper too — the map's label
 * halo is paper — so a hairline marks the seam. From lg the map takes the
 * wider column and spans both rows; the heading (with its one-line intro) and
 * the figures (with the link) keep their DOM order around the map and sit
 * either side of the column's middle. `grid-cols-1` matters below lg: an
 * implicit `auto` track would size to the map's 34rem phone minimum and push
 * the page past the viewport.
 *
 * The three figures come in like credits (`data-reveal`, one per row). Rows
 * share one footprint: the number sits in a fixed 11rem slot from sm up, so
 * the labels start on one line whatever the number's width.
 */
export function ZoneBand() {
  const copy = useCopy()
  const locale = useLocale()
  const z = copy.zone
  const figures: readonly [string, string][] = [
    [String(VALLEY_COUNT), z.home.figures.places],
    [`${grouped(Math.min(...ALTITUDES), locale)}–${grouped(Math.max(...ALTITUDES), locale)} m`, z.home.figures.altitude],
    [`${Math.max(...DISTANCES)} km`, z.home.figures.distance],
  ]
  return (
    <Section id="zone" className="border-t border-vv2-hairline" letterbox>
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-x-16 lg:gap-y-10">
        <div className="lg:self-end">
          <Eyebrow>{z.eyebrow}</Eyebrow>
          <h2 className="vv2-display mt-4 text-4xl">{z.title}</h2>
          <p className="mt-4 max-w-md text-vv2-ink-soft">{z.home.intro}</p>
        </div>
        <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <ZoneMap compact />
        </div>
        <div className="lg:self-start">
          <ul className="divide-y divide-vv2-hairline border-y border-vv2-hairline">
            {figures.map(([value, label]) => (
              <li key={label} data-reveal className="py-4 sm:flex sm:items-baseline sm:gap-5">
                <span className="vv2-display block whitespace-nowrap text-[1.75rem] leading-tight tabular-nums sm:w-44 sm:shrink-0">
                  {value}
                </span>
                <span className="mt-1 block text-vv2-ink-soft sm:mt-0">{label}</span>
              </li>
            ))}
          </ul>
          <Link
            to={pathFor('zone', locale)}
            className="mt-8 inline-block text-sm font-semibold text-vv2-ink underline decoration-vv2-moss underline-offset-4 hover:decoration-current"
          >
            {copy.servicesSection.more} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </Section>
  )
}
